import { useCallback, useEffect, useMemo, useState } from 'react';

import { Building, Check, Search, Settings2, Users } from 'lucide-react';
import { AiOutlineUserSwitch } from 'react-icons/ai';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/hooks/useAuth';
import { cn } from '@/lib/utils/cn';
import { apiFetchClients } from '@/services/clients.api';
import { TextToolTip } from './TextToolTip';

const ClientSwitcher = () => {
  const { isAuthenticated } = useAuth();

  const isEnabled = ((import.meta.env.VITE_ENV_INSTANCE === 'DEV' ||
    import.meta.env.VITE_ENV_INSTANCE === 'STAGING') &&
    !isAuthenticated) as boolean;

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id') || null;

  const [selectedClient, setSelectedClient] = useLocalStorage(
    'localClient',
    null
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['fetchClients'],
    queryFn: apiFetchClients,
    enabled: isEnabled,
    refetchOnWindowFocus: false,
  });

  const clients = useMemo(() => data?.data?.data || [], [data]);

  const filteredClients = useMemo(
    () =>
      clients.filter(
        (client) =>
          client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.organizationName
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      ),
    [clients, searchTerm]
  );

  const clientList = [
    ...filteredClients.filter((client) => client._id === selectedClient?._id),
    ...filteredClients.filter((client) => client._id !== selectedClient?._id),
  ];

  const clearAllCookies = () => {
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  };

  const handleSelectClient = useCallback(
    (client: any) => {
      if (client._id !== selectedClient?._id) {
        console.log(`Switching to client: ${client.name} (${client._id})`);

        // Clear storages
        sessionStorage.clear();
        localStorage.clear();

        // Clear cookies
        clearAllCookies();

        // Update selected client
        setSelectedClient(client);

        // Reload the page
        window.location.reload();
      }
    },
    [selectedClient, setSelectedClient]
  );

  useEffect(() => {
    if (clientId) {
      console.log(`Client ID from URL: ${clientId}`);
      console.log({ clients });
      const client = clients?.find((client) => client._id === clientId);
      if (client) {
        console.log(`Client found: ${client.name} (${client._id})`);
        handleSelectClient(client);
      } else {
        console.warn(`Client with ID ${clientId} not found`);
      }
    }
  }, [clientId, clients, handleSelectClient]);

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <TextToolTip label="Switch client">
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="!h-fit !w-fit !p-0 !border-0 hover:!bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:bg-transparent"
          >
            <Settings2 size={16} />
          </Button>
        </DialogTrigger>
      </TextToolTip>
      <DialogContent className="max-w-5xl h-[95vh] flex flex-col items-center justify-center gap-6 p-10">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-5xl w-full overflow-y-auto py-4">
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <Card key={i} className="h-48 p-6">
                  <div className="flex flex-col items-center justify-center gap-3 h-full">
                    <Skeleton className="h-16 w-16 rounded-full" />
                    <div className="space-y-2 text-center">
                      <Skeleton className="h-4 w-28 mx-auto" />
                      <Skeleton className="h-3 w-36 mx-auto" />
                    </div>
                  </div>
                </Card>
              ))}
          </div>
        ) : (
          <>
            {filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
                <Search className="w-16 h-16 opacity-20" />
                <p className="text-lg">
                  No organizations found matching "{searchTerm}"
                </p>
                <Button variant="outline" onClick={clearSearch}>
                  Clear Search
                </Button>
              </div>
            ) : (
              <>
                <DialogHeader className="space-y-3 w-full max-w-2xl">
                  <DialogTitle
                    className={cn(
                      'text-3xl font-bold text-center flex items-center justify-center gap-2'
                    )}
                  >
                    <AiOutlineUserSwitch size={'26'} className="text-primary" />
                    Switch Client
                  </DialogTitle>
                  <DialogDescription className="text-center max-w-md mx-auto">
                    Select a different client to view or manage their data
                  </DialogDescription>

                  <div className="flex justify-center">
                    <div className="relative w-full max-w-md">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search client or organization name"
                        className="pl-10 pr-4 py-2 w-full border border-border rounded-md bg-background text-sm focus:outline-none     focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  </div>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-w-3xl w-full gap-6 overflow-x-hidden overflow-y-auto p-4">
                  {clientList.map((client) => {
                    const isSelected = client?._id === selectedClient?._id;
                    return (
                      <Card
                        key={client?._id}
                        className={cn(
                          'relative h-48 w-full flex flex-col items-center border-dashed justify-center gap-3 p-6 transition-all duration-300 cursor-pointer group',
                          isSelected
                            ? 'border-primary'
                            : 'hover:border-primary/50'
                        )}
                        onClick={() => handleSelectClient(client)}
                      >
                        {isSelected && (
                          <div className="absolute -top-2 -right-2 bg-success rounded-full p-1 border-2 border-background shadow-md">
                            <Check
                              size={16}
                              className="text-success-foreground"
                            />
                          </div>
                        )}

                        <div className="relative">
                          <Avatar
                            className={cn(
                              'w-20 h-20 transition-all duration-300 border-2',
                              isSelected
                                ? 'ring-2 ring-primary ring-offset-2'
                                : 'group-hover:scale-110'
                            )}
                          >
                            <AvatarImage
                              className="object-contain p-2"
                              src={client?.logo}
                              alt={client?.name}
                            />
                            <AvatarFallback className="bg-muted text-2xl">
                              {client?.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        </div>

                        <div className="text-center space-y-1">
                          <h6
                            className={cn(
                              'font-semibold text-lg transition-colors duration-300',
                              isSelected && 'text-primary'
                            )}
                          >
                            {client?.name}
                          </h6>

                          <div
                            className={cn(
                              'flex items-center justify-center gap-1 text-sm text-muted-foreground',
                              !client?.organizationName && 'hidden'
                            )}
                          >
                            <Building className="w-3 h-3" />
                            <p className="line-clamp-1">
                              {client?.organizationName}
                            </p>
                          </div>
                          {client?.userCount && (
                            <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                              <Users className="w-3 h-3" />
                              <span>{client?.userCount} team members</span>
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClientSwitcher;
